/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { View } from 'react-native';
import TextFieldView from '@airasia-phoenix/react-native/components/TextFieldView';
import { TextFieldRefs } from '@airasia-phoenix/react-native/components/TextFieldView/types';
import { pd, getColor } from '@airasia-phoenix/react-native/utils/utils';
import { useTranslation } from 'react-i18next';
import { AdditionalLabels } from '../../config/constants';
import { CabinClassType } from './types';
import type { PassengerClassFieldProps, SelectedValues } from './types';
import { useDeviceInfo } from '@airasia-phoenix/react-native/utils';
import type { BottomSheetRefs } from '@airasia-phoenix/react-native/components/BottomSheetView';
import PassengerClassBottomSheetView from './PassengerClassBottomSheetView';
import { insertParamsInUrl } from '../../util/utils';
import { getCurrentBreakpoint } from '@airasia-rn/ui/utils';
import { useDesktopWebHover } from '../../hooks/useDesktopWebHover';

const PassengerClassFieldView = (props: PassengerClassFieldProps) => {
  const {
    adultCount,
    updateAdultCount,
    childCount,
    updateChildCount,
    infantCount,
    updateInfantCount,
    cabinClass,
    updateCabinClass,
    autoSearch,
    disabled,
    hideCabinClass,
  } = props;

  const { t } = useTranslation('flights');
  const { isWeb, isMobileBrowser } = useDeviceInfo();
  // const isWebAndNotMobile = isWeb && !isMobileBrowser;
  // const currentBreakpoint = getCurrentBreakpoint();
  const isDesktopView = getCurrentBreakpoint() > 1;

  const textFieldRef = useRef<TextFieldRefs>(null);
  const bottomSheetViewRef = useRef<BottomSheetRefs>(null);
  const anchorRef = useRef<View>(null);

  const [isModalVisible, setModalVisible] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  // TextFieldView's own hover is dead when editable={false}, so drive it here (desktop web only)
  const { isHovered, hoverProps } = useDesktopWebHover(!disabled);

  const guestsLabel = t('flightsSearchPage.main.pluralGuestLabel');
  const guestLabel = t('flightsSearchPage.main.singularGuestLabel');
  const guestAndTravelClassLabel = t(
    'flightsSearchPage.main.guest_and_travel_class',
  );

  const economyLabel = t('flightsSearchPage.cabinClass.economy');
  const businessLabel = t('flightsSearchPage.cabinClass.business');
  const firstClassLabel = t('flightsSearchPage.cabinClass.first_class');
  const premiumEconomyLabel = t('flightsSearchPage.cabinClass.premium_economy');

  const initialValuesRef = useRef<SelectedValues | null>(null);

  const cabinClassLabelMap: Record<CabinClassType, string> = useMemo(
    () => ({
      [CabinClassType.ECONOMY]: economyLabel,
      [CabinClassType.BUSINESS]: businessLabel,
      [CabinClassType.PREMIUM_ECONOMY]: premiumEconomyLabel,
      [CabinClassType.FIRST_CLASS]: firstClassLabel,
    }),
    [economyLabel, businessLabel, premiumEconomyLabel, firstClassLabel],
  );

  const buildDisplayValue = useCallback(
    (v: SelectedValues) => {
      const total = v.adult + v.child + v.infant;
      if (hideCabinClass) {
        return `${total} ${total > 1 ? guestsLabel : guestLabel}`;
      }
      return insertParamsInUrl(
        AdditionalLabels.PASSENGER_TEXT_FIELD_DISPLAY_STRING_FORMAT,
        [
          total.toString(),
          total > 1 ? guestsLabel : guestLabel,
          cabinClassLabelMap[v.cabinClass] ?? '',
        ],
      );
    },
    [cabinClassLabelMap, guestsLabel, guestLabel, hideCabinClass],
  );

  const [value, setValue] = useState(() =>
    buildDisplayValue({
      adult: adultCount,
      child: childCount,
      infant: infantCount,
      cabinClass,
    }),
  );

  useEffect(() => {
    setValue(
      buildDisplayValue({
        adult: adultCount,
        child: childCount,
        infant: infantCount,
        cabinClass,
      }),
    );
  }, [adultCount, childCount, infantCount, cabinClass, buildDisplayValue]);

  const openPicker = useCallback(() => {
    if (isDesktopView) {
      // store initial values
      initialValuesRef.current = {
        adult: adultCount,
        child: childCount,
        infant: infantCount,
        cabinClass,
      };
      setModalVisible(true);
      return;
    }

    if (isSheetOpen) return;
    setIsSheetOpen(true);
    bottomSheetViewRef.current?.toggleBottomSheetView();
  }, [
    isDesktopView,
    isSheetOpen,
    infantCount,
    childCount,
    adultCount,
    cabinClass,
  ]);

  const closeModal = useCallback(
    (e?: any) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      const initial = initialValuesRef.current;

      if (initial) {
        const hasChanged =
          initial.adult !== adultCount ||
          initial.child !== childCount ||
          initial.infant !== infantCount ||
          initial.cabinClass !== cabinClass;

        if (hasChanged) {
          const updatedValues = {
            adult: adultCount,
            child: childCount,
            infant: infantCount,
            cabinClass,
          };

          autoSearch?.(updatedValues);
        }
      }

      setTimeout(() => setModalVisible(false), 0);
    },
    [adultCount, childCount, infantCount, cabinClass, autoSearch],
  );

  const onSheetDidClose = useCallback((e?: any) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setIsSheetOpen(false);
    textFieldRef.current?.blurWithText?.();
  }, []);

  const handleDone = useCallback(
    (selected: SelectedValues, e?: any) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();

      setValue(buildDisplayValue(selected));
      textFieldRef.current?.blurWithText?.();

      if (isDesktopView) {
        setTimeout(() => setModalVisible(false), 0);
      } else {
        setIsSheetOpen(false);
      }
    },
    [buildDisplayValue, isDesktopView],
  );

  return (
    <View>
      <View ref={anchorRef} collapsable={false} {...hoverProps}>
        <TextFieldView
          ref={textFieldRef}
          startIcon={{
            icon: pd.icon.main.social.people_outline,
            tint: getColor(pd.sys.colour.content_secondary),
          }}
          editable={false}
          disabled={!!disabled}
          label={guestAndTravelClassLabel}
          value={value}
          onPress={openPicker}
          highlightBorder={isDesktopView && (isModalVisible || isHovered)}
        />
      </View>

      <PassengerClassBottomSheetView
        anchorRef={anchorRef}
        isModalVisible={isModalVisible}
        onRequestCloseModal={closeModal}
        isSheetOpen={isSheetOpen}
        onSheetDidClose={onSheetDidClose}
        bottomSheetViewRef={bottomSheetViewRef}
        adultCount={adultCount}
        childCount={childCount}
        infantCount={infantCount}
        cabinClass={cabinClass}
        updateAdultCount={updateAdultCount}
        updateChildCount={updateChildCount}
        updateInfantCount={updateInfantCount}
        updateCabinClass={updateCabinClass}
        onDone={handleDone}
        hideCabinClass={hideCabinClass}
      />
    </View>
  );
};

export default PassengerClassFieldView;
